// ===========================================================================
// Config-driven aggregation over a flat array of records
//
// The screen keeps ONE array of objects and never pre-shapes it. Each widget
// says, in configuration, how it wants that array cut — which rows, one bar per
// what, stacked by what, which measure and how it is aggregated — and
// aggregate() produces exactly that. Nothing here knows about Moonlake: field
// names arrive in the spec.
// ===========================================================================

export type AggFn = "sum" | "count" | "avg" | "min" | "max";

/**
 * How a list of values is ordered:
 *   "alpha"        — alphabetical;
 *   "total-desc"   — largest aggregate first (aggregate() only);
 *   { by: field }  — by another field of the same records, ascending (e.g.
 *                    snapshot by snapshot_dt), so the order comes from the data;
 *   string[]       — an explicit order; values not listed go last, alphabetical.
 */
export type Order<T> = "alpha" | "total-desc" | { by: keyof T } | string[];

/** field → allowed value(s). A record passes when every listed field matches. */
export type Where<T> = { [K in keyof T]?: string | number | (string | number)[] };

export interface AggSpec<T> {
  where?: Where<T>;
  /** One output row per distinct value of this field. */
  groupBy: keyof T;
  order?: Order<T>;
  /** Split each output row by this field's values. */
  stackBy: keyof T;
  /**
   * Re-key a raw stackBy value (e.g. fold 3L, 4L, 5L … into 3L+). Gets the whole
   * record too, so the rule can read other fields (bucket_type). Return null to
   * drop the record. Identity when omitted.
   */
  stackMap?: (value: string, record: T) => string | null;
  /** The stack keys every output row carries, zero-filled. */
  stackKeys: string[];
  /** The numeric field aggregated. Ignored by "count". */
  measure: keyof T;
  fn: AggFn;
}

export interface AggRow {
  name: string;
  /** Sum of the row's stack values. */
  total: number;
  [stackKey: string]: number | string;
}

export function matchesWhere<T>(r: T, where?: Where<T>): boolean {
  if (!where) return true;
  for (const k of Object.keys(where) as (keyof T)[]) {
    const want = where[k];
    if (want === undefined) continue;
    const v = r[k] as any;
    if (Array.isArray(want) ? !want.some((w) => w == v) : want != v) return false;
  }
  return true;
}

/** Distinct values of `field` across `rows`, ordered per `order`. */
export function distinctValues<T>(rows: T[], field: keyof T, order: Order<T> = "alpha"): string[] {
  const values = [...new Set(rows.map((r) => String(r[field] ?? "")).filter(Boolean))];
  return sortValues(values, rows, field, order);
}

function sortValues<T>(
  values: string[],
  rows: T[],
  field: keyof T,
  order: Order<T>,
  totals?: Map<string, number>
): string[] {
  const alpha = (a: string, b: string) => a.localeCompare(b);
  if (Array.isArray(order)) {
    const idx = (v: string) => {
      const i = order.indexOf(v);
      return i < 0 ? Number.MAX_SAFE_INTEGER : i;
    };
    return values.sort((a, b) => idx(a) - idx(b) || alpha(a, b));
  }
  if (order === "total-desc" && totals) {
    return values.sort((a, b) => (totals.get(b) || 0) - (totals.get(a) || 0) || alpha(a, b));
  }
  if (typeof order === "object" && "by" in order) {
    // Each value's sort key is the smallest `by` seen on its records. Compared
    // numeric-aware, so a number field (month 3 < 12) and an ISO date both order.
    const cmp = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });
    const key = new Map<string, string>();
    rows.forEach((r) => {
      const v = String(r[field] ?? "");
      const k = String(r[order.by] ?? "");
      if (!key.has(v) || cmp(k, key.get(v)!) < 0) key.set(v, k);
    });
    return values.sort((a, b) => cmp(key.get(a) || "", key.get(b) || "") || alpha(a, b));
  }
  return values.sort(alpha);
}

interface Acc {
  sum: number;
  count: number;
  min: number;
  max: number;
}

function finish(acc: Acc | undefined, fn: AggFn): number {
  if (!acc || !acc.count) return 0;
  switch (fn) {
    case "count":
      return acc.count;
    case "avg":
      return acc.sum / acc.count;
    case "min":
      return acc.min;
    case "max":
      return acc.max;
    default:
      return acc.sum;
  }
}

/**
 * Side-by-side series (grouped bars, lines): one output row per `groupBy` value,
 * one value per series. Unlike a stack, a series can be a SHARE, so each cell
 * keeps its numerator and denominator:
 *
 *   "abs"         — the summed measure (counting only `numerator` records);
 *   "share-cell"  — numerator ÷ every record of the same cell (e.g. a drug's
 *                   covered lives ÷ that drug's lives in that channel);
 *   "share-group" — numerator ÷ every record of the same row, all series (e.g.
 *                   one access split ÷ the channel's addressable patients);
 *   "ratio"       — numerator ÷ denominator as it stands, not a percentage
 *                   (e.g. months on therapy ÷ patients: an average).
 *
 * Shares are percentages (0–100). Without `seriesBy` every record is one series,
 * `seriesKeys[0]`. Each row also carries `<key>__n` and
 * `<key>__d`, the cell's numerator and denominator, for tooltips and exports.
 */
export interface SeriesSpec<T> {
  where?: Where<T>;
  groupBy: keyof T;
  order?: Order<T>;
  seriesBy?: keyof T;
  seriesKeys: string[];
  measure: keyof T;
  /** The denominator's field; `measure` when omitted. */
  denominator?: keyof T;
  /** Records whose measure counts toward the value. Every record when omitted. */
  numerator?: (r: T) => boolean;
  mode: SeriesMode;
}

export type SeriesMode = "abs" | "share-cell" | "share-group" | "ratio";

export function aggregateSeries<T>(rows: T[], spec: SeriesSpec<T>): AggRow[] {
  const den = spec.denominator || spec.measure;
  const cells = new Map<string, Map<string, { n: number; d: number }>>();
  const groupDen = new Map<string, number>();
  const kept: T[] = [];

  rows.forEach((r) => {
    if (!matchesWhere(r, spec.where)) return;
    const key = spec.seriesBy ? String(r[spec.seriesBy] ?? "") : spec.seriesKeys[0];
    if (!spec.seriesKeys.includes(key)) return;
    const name = String(r[spec.groupBy] ?? "");
    if (!name) return;
    kept.push(r);

    let group = cells.get(name);
    if (!group) cells.set(name, (group = new Map()));
    let cell = group.get(key);
    if (!cell) group.set(key, (cell = { n: 0, d: 0 }));
    const d = Number(r[den]) || 0;
    cell.d += d;
    groupDen.set(name, (groupDen.get(name) || 0) + d);
    if (!spec.numerator || spec.numerator(r)) cell.n += Number(r[spec.measure]) || 0;
  });

  const out = new Map<string, AggRow>();
  cells.forEach((group, name) => {
    const row: AggRow = { name, total: 0 };
    spec.seriesKeys.forEach((k) => {
      const cell = group.get(k) || { n: 0, d: 0 };
      const d = spec.mode === "share-group" ? groupDen.get(name) || 0 : cell.d;
      const v =
        spec.mode === "abs" ? cell.n : !d ? 0 : spec.mode === "ratio" ? cell.n / d : (cell.n / d) * 100;
      row[k] = v;
      row[`${k}__n`] = cell.n;
      row[`${k}__d`] = d;
      row.total += cell.n;
    });
    out.set(name, row);
  });

  const totals = new Map([...out.values()].map((r) => [r.name, r.total]));
  const names = sortValues([...out.keys()], kept, spec.groupBy, spec.order || "alpha", totals);
  return names.map((n) => out.get(n)!);
}

/**
 * A relative index: per `groupBy` value, the focus records' rate (measure ÷
 * denominator) over the reference records' rate — 1 is parity, above 1 better.
 * One series, `key`; a row where either side has no denominator is left out.
 * `<key>__n` / `<key>__d` carry the focus and reference rates.
 */
export interface IndexSpec<T> {
  where?: Where<T>;
  groupBy: keyof T;
  order?: Order<T>;
  key: string;
  measure: keyof T;
  denominator: keyof T;
  /** The records measured; every other record is the reference. */
  focus: (r: T) => boolean;
}

export function aggregateIndex<T>(rows: T[], spec: IndexSpec<T>): AggRow[] {
  const cells = new Map<string, { fn: number; fd: number; rn: number; rd: number }>();
  const kept: T[] = [];
  rows.forEach((r) => {
    if (!matchesWhere(r, spec.where)) return;
    const name = String(r[spec.groupBy] ?? "");
    if (!name) return;
    kept.push(r);
    let c = cells.get(name);
    if (!c) cells.set(name, (c = { fn: 0, fd: 0, rn: 0, rd: 0 }));
    const n = Number(r[spec.measure]) || 0;
    const d = Number(r[spec.denominator]) || 0;
    if (spec.focus(r)) {
      c.fn += n;
      c.fd += d;
    } else {
      c.rn += n;
      c.rd += d;
    }
  });

  const out = new Map<string, AggRow>();
  cells.forEach((c, name) => {
    if (!c.fd || !c.rd || !c.rn) return;
    const focus = c.fn / c.fd;
    const reference = c.rn / c.rd;
    const v = focus / reference;
    out.set(name, { name, total: v, [spec.key]: v, [`${spec.key}__n`]: focus, [`${spec.key}__d`]: reference });
  });

  const totals = new Map([...out.values()].map((r) => [r.name, r.total]));
  const names = sortValues([...out.keys()], kept, spec.groupBy, spec.order || "alpha", totals);
  return names.map((n) => out.get(n)!);
}

/** Filter → group → stack → aggregate, as the spec says. */
export function aggregate<T>(rows: T[], spec: AggSpec<T>): AggRow[] {
  const cells = new Map<string, Map<string, Acc>>();
  const kept: T[] = [];

  rows.forEach((r) => {
    if (!matchesWhere(r, spec.where)) return;
    const raw = String(r[spec.stackBy] ?? "");
    const key = spec.stackMap ? spec.stackMap(raw, r) : raw;
    if (key === null || !spec.stackKeys.includes(key)) return;
    const name = String(r[spec.groupBy] ?? "");
    if (!name) return;
    kept.push(r);

    let group = cells.get(name);
    if (!group) cells.set(name, (group = new Map()));
    let acc = group.get(key);
    if (!acc) group.set(key, (acc = { sum: 0, count: 0, min: Infinity, max: -Infinity }));
    const v = Number(r[spec.measure]) || 0;
    acc.sum += v;
    acc.count += 1;
    acc.min = Math.min(acc.min, v);
    acc.max = Math.max(acc.max, v);
  });

  const out = new Map<string, AggRow>();
  cells.forEach((group, name) => {
    const row: AggRow = { name, total: 0 };
    spec.stackKeys.forEach((k) => {
      const v = finish(group.get(k), spec.fn);
      row[k] = v;
      row.total += v;
    });
    out.set(name, row);
  });

  const totals = new Map([...out.values()].map((r) => [r.name, r.total]));
  const names = sortValues([...out.keys()], kept, spec.groupBy, spec.order || "alpha", totals);
  return names.map((n) => out.get(n)!);
}
